import { useEffect, useMemo, useState, useCallback } from 'react';
import { Search, X, UserPlus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'sonner';
import { PAPER, INPUT_CLASS } from '../theme';

/**
 * LabourPayload — the shape stored in DiaryEntry.payload for `type: labour`.
 *
 *   {
 *     people: [
 *       { source: 'employee'|'subcontractor'|'manual', id?, name, trade?, note? },
 *       ...
 *     ]
 *   }
 *
 * `source: manual` covers a person not in either database. Spec §5 allows
 * optionally saving a manual person to Sub-contractors for next time via
 * a placeholder email — an admin can update it in Contacts later.
 */

const emptyManualDraft = { name: '', trade: '', save_to_contacts: false };

export const LabourForm = ({ value, onChange, readOnly = false }) => {
  const { api, isAdmin, isSurveyor } = useAuth();
  const canUseHours = (isAdmin || isSurveyor) && !readOnly;
  const [employees, setEmployees] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [manualDraft, setManualDraft] = useState(emptyManualDraft);
  const [showManual, setShowManual] = useState(false);

  const selected = useMemo(() => value?.people || [], [value]);
  const isSelected = useCallback((source, id) => {
    return selected.some(p => p.source === source && p.id === id);
  }, [selected]);

  useEffect(() => {
    (async () => {
      try {
        // Both employees AND sub-contractors live in Staff Management
        // (db.users) and carry their day rate on `rate_per_hour`. Split
        // the single /employees feed by user_type so the diary labour
        // total can cost everyone against their stored day rate.
        const res = await api.get('/employees');
        const all = res.data || [];
        setEmployees(all.filter(p => (p.user_type || 'employee') === 'employee'));
        setSubs(all.filter(p => p.user_type === 'sub_contractor'));
      } catch (_e) {
        toast.error('Failed to load labour list');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const togglePerson = (source, id, name, trade) => {
    const exists = selected.find(p => p.source === source && p.id === id);
    const next = exists
      ? selected.filter(p => !(p.source === source && p.id === id))
      : [...selected, { source, id, name, trade }];
    onChange({ ...(value || {}), people: next });
  };

  const addManualPerson = async () => {
    if (!manualDraft.name.trim()) {
      toast.error('Add a name for the manual entry');
      return;
    }
    const person = {
      source: 'manual',
      id: `manual_${Date.now()}`,
      name: manualDraft.name.trim(),
      trade: manualDraft.trade.trim() || undefined,
    };
    onChange({ ...(value || {}), people: [...selected, person] });

    if (manualDraft.save_to_contacts) {
      try {
        // Unique placeholder email so repeated manual adds don't collide
        // with the /contacts email-uniqueness guard.
        const slug = (person.name || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'unknown';
        const placeholderEmail = `no-email+${slug}-${Date.now().toString(36)}@lux.local`;
        await api.post('/contacts', {
          name: person.name,
          company: person.name,
          category: 'Sub-contractor',
          trade: person.trade,
          telephone: '07777777777',
          email: placeholderEmail,
        });
        toast.success('Saved to sub-contractors');
      } catch (_e) {
        toast.error('Added to today but could not save to Sub-contractors');
      }
    }
    setManualDraft(emptyManualDraft);
    setShowManual(false);
  };

  const filterList = useCallback((list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(p => (p.name || '').toLowerCase().includes(q) || (p.trade || '').toLowerCase().includes(q));
  }, [search]);

  const filteredEmployees = useMemo(() => filterList(employees), [employees, filterList]);
  const filteredSubs = useMemo(() => filterList(subs), [subs, filterList]);

  const setPersonNote = (source, id, note) => {
    onChange({
      ...(value || {}),
      people: selected.map(p => (p.source === source && p.id === id) ? { ...p, note } : p),
    });
  };

  // Admin/surveyor only — switch a person between a full day and a number of
  // hours. Default stays 'day'; switching to hours seeds 8 (a full-day
  // equivalent) so nothing is silently under-costed until they adjust it.
  const setPersonUnit = (source, id, unit) => {
    onChange({
      ...(value || {}),
      people: selected.map(p => (p.source === source && p.id === id)
        ? { ...p, unit, hours: unit === 'hours' ? (p.hours ?? 8) : undefined }
        : p),
    });
  };
  const setPersonHours = (source, id, hours) => {
    onChange({
      ...(value || {}),
      people: selected.map(p => (p.source === source && p.id === id) ? { ...p, hours } : p),
    });
  };

  return (
    <div className="space-y-3">
      {/* Selected chips summary */}
      {selected.length > 0 && (
        <div
          className="rounded-lg px-3 py-2.5"
          style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold mb-2" style={{ color: PAPER.muted }}>
            On site today · {selected.length}
          </div>
          <div className="space-y-2">
            {selected.map(p => {
              const isHours = p.unit === 'hours';
              return (
              <div key={`${p.source}:${p.id}`} className="rounded-md" style={{ background: PAPER.page, border: `1px solid ${PAPER.divider}` }}>
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <span className="flex-1 text-[13px] truncate" style={{ color: PAPER.ink }}>
                    {p.name}
                    {p.trade && <span style={{ color: PAPER.muted }}> · {p.trade}</span>}
                    {p.source === 'manual' && (
                      <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#B45309' }}>manual</span>
                    )}
                    {isHours && (
                      <span className="ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: '#DBEAFE', color: '#1E40AF' }} data-testid={`labour-hours-badge-${p.source}-${p.id}`}>{p.hours ?? 0}h</span>
                    )}
                  </span>
                  <input
                    type="text"
                    value={p.note || ''}
                    onChange={(e) => setPersonNote(p.source, p.id, e.target.value)}
                    placeholder="Optional note"
                    className="w-28 text-[12px] rounded px-2 py-1 focus:outline-none focus:border-[#F5A623]"
                    style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
                  />
                  {!readOnly && (
                  <button
                    type="button"
                    onClick={() => togglePerson(p.source, p.id, p.name, p.trade)}
                    className="p-1 rounded hover:bg-red-50 transition-colors"
                    style={{ color: PAPER.muted }}
                    title="Remove"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  )}
                </div>
                {canUseHours && (
                  <div className="flex items-center gap-2 px-2 pb-1.5 flex-wrap">
                    <div className="inline-flex rounded-md overflow-hidden" style={{ border: `1px solid ${PAPER.border}` }}>
                      <button type="button" onClick={() => setPersonUnit(p.source, p.id, 'day')} className="text-[11px] px-2.5 py-1 font-semibold transition-colors" style={{ background: !isHours ? PAPER.accent : PAPER.card, color: !isHours ? PAPER.navy : PAPER.inkSoft }} data-testid={`labour-unit-day-${p.source}-${p.id}`}>Full day</button>
                      <button type="button" onClick={() => setPersonUnit(p.source, p.id, 'hours')} className="text-[11px] px-2.5 py-1 font-semibold transition-colors" style={{ background: isHours ? PAPER.accent : PAPER.card, color: isHours ? PAPER.navy : PAPER.inkSoft, borderLeft: `1px solid ${PAPER.border}` }} data-testid={`labour-unit-hours-${p.source}-${p.id}`}>Hours</button>
                    </div>
                    {isHours && (
                      <div className="inline-flex items-center gap-1.5">
                        <input type="number" inputMode="decimal" min="0.5" max="24" step="0.5" value={p.hours ?? ''} onChange={(e) => setPersonHours(p.source, p.id, e.target.value === '' ? '' : Number(e.target.value))} className="w-16 text-[12px] rounded px-2 py-1 focus:outline-none focus:border-[#F5A623]" style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }} data-testid={`labour-hours-input-${p.source}-${p.id}`} />
                        <span className="text-[11px]" style={{ color: PAPER.muted }}>hrs · of an 8h day</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      )}

      {/* Search + pickers + manual add — hidden in read-only. In view mode
          the "On site today" summary above is the only view users need. */}
      {!readOnly && (
      <>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 pointer-events-none" style={{ color: PAPER.muted }} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter by name or trade"
          className={`${INPUT_CLASS} pl-9`}
        />
      </div>

      {loading ? (
        <div className="text-center text-[12px] py-4" style={{ color: PAPER.muted }}>Loading…</div>
      ) : (
        <>
          <PickerGroup title="Employees" people={filteredEmployees} source="employee" isSelected={isSelected} toggle={togglePerson} />
          <PickerGroup title="Sub-contractors" people={filteredSubs} source="subcontractor" isSelected={isSelected} toggle={togglePerson} />
        </>
      )}

      {/* Manual add */}
      {!showManual ? (
        <button
          type="button"
          onClick={() => setShowManual(true)}
          className="w-full flex items-center justify-center gap-1.5 text-[13px] font-medium py-2 hover:underline"
          style={{ color: PAPER.accent }}
        >
          <UserPlus className="w-3.5 h-3.5" />
          Add someone not listed
        </button>
      ) : (
        <div
          className="rounded-lg p-3 space-y-2"
          style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
        >
          <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
            One-off add
          </div>
          <input
            type="text"
            value={manualDraft.name}
            onChange={(e) => setManualDraft(d => ({ ...d, name: e.target.value }))}
            placeholder="Name"
            className={INPUT_CLASS}
          />
          <input
            type="text"
            value={manualDraft.trade}
            onChange={(e) => setManualDraft(d => ({ ...d, trade: e.target.value }))}
            placeholder="Trade (optional)"
            className={INPUT_CLASS}
          />
          <label className="flex items-center gap-2 text-[12px]" style={{ color: PAPER.inkSoft }}>
            <input
              type="checkbox"
              checked={manualDraft.save_to_contacts}
              onChange={(e) => setManualDraft(d => ({ ...d, save_to_contacts: e.target.checked }))}
              className="accent-[#F5A623]"
            />
            Save to sub-contractors for next time
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setShowManual(false); setManualDraft(emptyManualDraft); }}
              className="flex-1 text-[13px] font-medium rounded-md py-2 transition-colors hover:bg-[#FAF7F2]"
              style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.inkSoft }}
            >Cancel</button>
            <button
              type="button"
              onClick={addManualPerson}
              className="flex-1 text-[13px] font-semibold rounded-md py-2"
              style={{ background: PAPER.navy, color: PAPER.accent }}
            >Add</button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

const PickerGroup = ({ title, people, source, isSelected, toggle }) => {
  if (!people.length) return null;
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: PAPER.muted }}>
        {title} · {people.length}
      </div>
      <div
        className="rounded-lg max-h-56 overflow-y-auto"
        style={{ background: PAPER.card, border: `1px solid ${PAPER.border}` }}
      >
        {people.map(p => {
          const active = isSelected(source, p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => toggle(source, p.id, p.name, p.trade)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors last:border-b-0"
              style={{
                borderBottom: `1px solid ${PAPER.divider}`,
                background: active ? '#FEF3C7' : 'transparent',
                color: active ? '#78350F' : PAPER.ink,
              }}
            >
              <span
                className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                style={{
                  background: active ? PAPER.accent : PAPER.card,
                  border: `1px solid ${active ? PAPER.accent : PAPER.border}`,
                }}
              >
                {active && <span className="text-[9px] font-bold" style={{ color: PAPER.navy }}>✓</span>}
              </span>
              <span className="flex-1 truncate">
                {p.name}
                {p.trade && <span style={{ color: PAPER.muted }}> · {p.trade}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
